import {
  createRootRoute,
  createRoute,
  redirect,
  type ParsedLocation,
} from "@tanstack/react-router";
import { Suspense } from "react";
import PageLayout from "@/components/layout/PageLayout";
import { RouteErrorFallback } from "@/components/layout/ErrorBoundary";
import { AdminPageSkeleton } from "@/features/admin/components/AdminPageSkeleton";
import { DashboardSkeleton } from "@/components/ui/DashboardSkeleton";
import { LoginSkeleton } from "@/features/auth/LoginSkeleton";
import { SignupSkeleton } from "@/features/auth/SignupSkeleton";
import { BookingSkeleton } from "@/features/booking/BookingSkeleton";
import { SpecialistProfileSkeleton } from "@/features/specialist-profile/SpecialistProfileSkeleton";
import { AppointmentTimeZone, UserRole } from "@/api/enums";
import { decodeJwt } from "@/api/hooks/user.hooks";
import { STORAGE_KEYS } from "@/constants/storage";
import NotFound from "@/components/layout/NotFound";
import Unauthorized from "@/components/layout/Unauthorized";
import { HomePage } from "./routes/home";
import { SearchPage } from "./routes/search";
import { CancellationPolicyPage } from "./routes/cancellation-policy";
import SpecialistProfile from "./features/specialist-profile/SpecialistProfile";
import BookingRequest from "./features/booking/BookingRequest";
import Confirmation from "./features/booking/Confirmation";
import AdminDashboardLayout from "./features/admin/dashboard";
import ManageAppointment from "./features/admin/manage-appointment/ManageAppointment";
import { PatientLayout } from "./features/client-area/components/PatientLayout";
import PatientDashboard from "./features/client-area/PatientDashboard";
import PatientAppointments from "./features/client-area/PatientAppointments";
import PatientAppointmentDetail from "./features/client-area/PatientAppointmentDetail";
import PatientProfile from "./features/client-area/PatientProfile";
import { LoginPage } from "./features/auth/LoginPage";
import { SignupPage } from "./features/auth/SignupPage";

function getUser() {
  const token = sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  return token ? decodeJwt(token) : null;
}

function requireAuth(requiredRoles?: UserRole[]) {
  return ({ location }: { location: ParsedLocation }) => {
    const user = getUser();
    if (!user)
      throw redirect({ to: "/login", search: { redirect: location.href } });
    if (
        requiredRoles?.length &&
        !requiredRoles.some((r) => user.roles.includes(r))
    ) {
      throw redirect({ to: "/unauthorized" });
    }
  };
}

function requireGuest() {
  return () => {
    const user = getUser();
    if (!user) return;
    const from = new URLSearchParams(window.location.search).get("redirect");
    if (from) throw redirect({ to: from });
    if (user.roles.includes(UserRole.Patient))
      throw redirect({ to: "/client-area" });
    throw redirect({ to: "/admin" });
  };
}

export const rootRoute = createRootRoute({
  component: () => <PageLayout />,
  errorComponent: RouteErrorFallback,
  notFoundComponent: NotFound,
});

export const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: () => (
      <Suspense fallback={null}>
        <HomePage />
      </Suspense>
  ),
});

export const searchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "search",
  // The router JSON-parses query values, so ?zipCode=10001 arrives as a number. Convert, don't cast.
  validateSearch: (search: Record<string, unknown>) => ({
    specialty: search.specialty == null ? "" : String(search.specialty),
    zipCode: search.zipCode == null ? "" : String(search.zipCode),
  }),
  component: () => (
      <Suspense fallback={null}>
        <SearchPage />
      </Suspense>
  ),
});

export const cancellationPolicyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "cancellation-policy",
  component: () => (
      <Suspense fallback={null}>
        <CancellationPolicyPage />
      </Suspense>
  ),
});

export const specialistProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "specialist-profile/$specialistSlug/$practiceSlug",
  component: () => (
      <Suspense fallback={<SpecialistProfileSkeleton />}>
        <SpecialistProfile />
      </Suspense>
  ),
});

export const bookingRoute = createRoute({
  getParentRoute: () => rootRoute,
  beforeLoad: requireAuth(),
  path: "booking",
  // The picked availability slot travels through the URL: `timeSlotId` plus the
  // slot's own `locationId` are what the booking form submits, so the visit type
  // it sends is always the one the API derives from that slot.
  validateSearch: (search: Record<string, unknown>) => ({
    specialistId: (search.specialistId as string) ?? "",
    timeSlotId: (search.timeSlotId as string) ?? "",
    locationId: (search.locationId as string) ?? undefined,
    locationAddress: (search.locationAddress as string) ?? undefined,
    appointmentDate: (search.appointmentDate as string) ?? "",
    timeZone:
        search.timeZone === AppointmentTimeZone.EST
            ? AppointmentTimeZone.EST
            : undefined,
    doctor: (search.doctor as string) ?? undefined,
  }),
  component: () => (
      <Suspense fallback={<BookingSkeleton />}>
        <BookingRequest />
      </Suspense>
  ),
});

export const confirmationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "confirmation",
  beforeLoad: requireAuth(),
  validateSearch: (search: Record<string, unknown>) => ({
    appointmentId: (search.appointmentId as string) ?? undefined,
  }),
  component: () => (
      <Suspense fallback={null}>
        <Confirmation />
      </Suspense>
  ),
});

export const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "admin",
  beforeLoad: requireAuth([
    UserRole.Admin,
    UserRole.SuperAdmin,
    UserRole.PracticeAdministrator,
  ]),
  component: () => (
      <Suspense fallback={<AdminPageSkeleton variant="dashboard" />}>
        <AdminDashboardLayout />
      </Suspense>
  ),
});

export const adminAppointmentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "admin/appointment/$id",
  beforeLoad: requireAuth([
    UserRole.Admin,
    UserRole.SuperAdmin,
    UserRole.PracticeAdministrator,
  ]),
  component: () => (
      <Suspense fallback={<AdminPageSkeleton variant="manageAppointment" />}>
        <ManageAppointment />
      </Suspense>
  ),
});

export const clientAreaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "client-area",
  beforeLoad: requireAuth([UserRole.Patient]),
  component: () => (
      <Suspense fallback={<DashboardSkeleton />}>
        <PatientLayout />
      </Suspense>
  ),
});

export const clientAreaIndexRoute = createRoute({
  getParentRoute: () => clientAreaRoute,
  path: "/",
  component: () => (
      <Suspense fallback={<DashboardSkeleton />}>
        <PatientDashboard />
      </Suspense>
  ),
});

export const clientAreaAppointmentsRoute = createRoute({
  getParentRoute: () => clientAreaRoute,
  path: "appointments",
  component: () => (
      <Suspense fallback={<DashboardSkeleton />}>
        <PatientAppointments />
      </Suspense>
  ),
});

export const clientAreaAppointmentDetailRoute = createRoute({
  getParentRoute: () => clientAreaRoute,
  path: "appointments/$id",
  component: () => (
      <Suspense fallback={<DashboardSkeleton />}>
        <PatientAppointmentDetail />
      </Suspense>
  ),
});

export const clientAreaProfileRoute = createRoute({
  getParentRoute: () => clientAreaRoute,
  path: "profile",
  component: () => (
      <Suspense fallback={<DashboardSkeleton />}>
        <PatientProfile />
      </Suspense>
  ),
});

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "login",
  beforeLoad: requireGuest(),
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: (search.redirect as string) ?? undefined,
    email: (search.email as string) ?? undefined,
  }),
  component: () => (
      <Suspense fallback={<LoginSkeleton />}>
        <LoginPage />
      </Suspense>
  ),
});

export const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "signup",
  beforeLoad: requireGuest(),
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: (search.redirect as string) ?? undefined,
    email: (search.email as string) ?? undefined,
  }),
  component: () => (
      <Suspense fallback={<SignupSkeleton />}>
        <SignupPage />
      </Suspense>
  ),
});

export const unauthorizedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "unauthorized",
  component: Unauthorized,
});

const clientAreaRouteWithChildren = clientAreaRoute.addChildren([
  clientAreaIndexRoute,
  clientAreaAppointmentsRoute,
  clientAreaAppointmentDetailRoute,
  clientAreaProfileRoute,
]);

export const routeTree = rootRoute.addChildren([
  indexRoute,
  searchRoute,
  cancellationPolicyRoute,
  specialistProfileRoute,
  bookingRoute,
  confirmationRoute,
  adminRoute,
  adminAppointmentRoute,
  clientAreaRouteWithChildren,
  loginRoute,
  signupRoute,
  unauthorizedRoute,
]);
